import { useCallback, useEffect, useRef, useState } from 'react';
import { QM_POLL } from './constants';
import { isRecordingLockHeld, uuid } from './browser';

/** A recording owned by another tab of this origin, as that tab last announced it. */
export interface RemoteRecording {
  recordingId: string;
  phase: 'recording' | 'paused' | 'saving';
  accumulatedMs: number;
  /** The owner's `Date.now()` when the current run began; same machine, same clock. */
  runningSince: number | null;
}

type Message =
  | ({ type: 'state'; tabId: string } & RemoteRecording)
  | { type: 'ended'; tabId: string; recordingId: string }
  | { type: 'hello'; tabId: string }
  | { type: 'stop'; tabId: string; recordingId: string };

interface Options {
  /** `clarix-quick-meeting:{userId}:{slug}`; null while signed out. */
  scope: string | null;
  /** This tab's recording, to answer a new tab's hello. */
  getOwnState: () => RemoteRecording | null;
  onStopRequested: (recordingId: string) => void;
}

/**
 * Cross-tab presence over a BroadcastChannel (QUICK-MEETING-PLAN §2.3): the owner tab announces
 * its recording, other tabs show "Recording in another tab" and can ask it to stop. A dead owner
 * is detected by its Web Lock being released, never by missed messages (hidden tabs throttle).
 */
export function useCrossTabRecording({ scope, getOwnState, onStopRequested }: Options) {
  const [tabId] = useState(() => uuid());
  const [remote, setRemote] = useState<{ scope: string; value: RemoteRecording } | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const handlersRef = useRef({ getOwnState, onStopRequested });
  useEffect(() => {
    handlersRef.current = { getOwnState, onStopRequested };
  });

  useEffect(() => {
    if (!scope || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(scope);
    channelRef.current = channel;
    channel.onmessage = (event: MessageEvent<Message>) => {
      const msg = event.data;
      if (!msg || msg.tabId === tabId) return;
      if (msg.type === 'hello') {
        const own = handlersRef.current.getOwnState();
        if (own) channel.postMessage({ type: 'state', tabId, ...own } satisfies Message);
      } else if (msg.type === 'state') {
        const { type: _type, tabId: _tab, ...value } = msg;
        setRemote({ scope, value });
      } else if (msg.type === 'ended') {
        setRemote((cur) => (cur?.value.recordingId === msg.recordingId ? null : cur));
      } else if (msg.type === 'stop') {
        const own = handlersRef.current.getOwnState();
        if (own?.recordingId === msg.recordingId) handlersRef.current.onStopRequested(msg.recordingId);
      }
    };
    channel.postMessage({ type: 'hello', tabId } satisfies Message);
    return () => {
      channel.close();
      if (channelRef.current === channel) channelRef.current = null;
    };
  }, [scope, tabId]);

  const current = remote && remote.scope === scope ? remote.value : null;

  // Drop a remote recording whose owner tab died (its lock was released).
  useEffect(() => {
    if (!current || !scope) return;
    const id = window.setInterval(() => {
      void isRecordingLockHeld(scope).then((held) => {
        if (held === false) setRemote(null);
      });
    }, QM_POLL.REMOTE_LIVENESS_MS);
    return () => window.clearInterval(id);
  }, [current, scope]);

  const announce = useCallback(
    (state: RemoteRecording) => {
      channelRef.current?.postMessage({ type: 'state', tabId, ...state } satisfies Message);
    },
    [tabId],
  );

  const announceEnded = useCallback(
    (recordingId: string) => {
      channelRef.current?.postMessage({ type: 'ended', tabId, recordingId } satisfies Message);
    },
    [tabId],
  );

  const requestRemoteStop = useCallback(() => {
    if (!current) return;
    channelRef.current?.postMessage({
      type: 'stop',
      tabId,
      recordingId: current.recordingId,
    } satisfies Message);
  }, [current, tabId]);

  return { remote: current, announce, announceEnded, requestRemoteStop };
}
